import { STORAGE_KEYS } from '../constants';

const SWIPEY_BASE_URL =
  process.env.REACT_APP_SWIPEY_BASE_URL || 'https://swipey.ai';

const CHARACTER_BY_ETHNICITY = {
  Asian: 'sora_tanaka',
  Caucasian: 'isabelle_davies',
};

const DEFAULT_CHARACTER = 'sora_tanaka';

// ---- Post-auth navigation config (GB flag: createaitrans-paywall-nav-config) ----
// Flag value is JSON: { type: 'paywall' | 'chat' | 'chat_paywall' }. Replaces the old
// `postSignupFlow` field that lived inside createaitrans-config.
//   'paywall'      → swipey.ai/paywall (purchase) — existing behaviour.
//   'chat'         → swipey.ai/chats/{slug} directly (plain chat).
//   'chat_paywall' → swipey.ai/lobby (paywalled chat). Everything else (auth_token,
//                    selected_slug, utm, …) is sent exactly as the paywall flow sends it —
//                    only the destination path changes. The model rides as `selected_slug`
//                    in the query, not in the path.

// localStorage key holding the resolved nav type across the OAuth round-trip / page reload.
const NAV_STORAGE_KEY = 'paywallNavType';

// Both 'chat' and 'chat_paywall' bypass the /paywall purchase step.
const isChatNav = (navType) => navType === 'chat' || navType === 'chat_paywall';

const getNavType = () => {
  try {
    return localStorage.getItem(NAV_STORAGE_KEY) || 'paywall';
  } catch {
    return 'paywall';
  }
};

export const getSelectedCharacterSlug = () => {
  try {
    const sel = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.AI_TRANS_SELECTIONS) || '{}',
    );
    const ethnicity = sel?.step1?.ethnicity;
    return CHARACTER_BY_ETHNICITY[ethnicity] || DEFAULT_CHARACTER;
  } catch {
    return DEFAULT_CHARACTER;
  }
};

// Persist the GB-resolved nav type at the moment the user kicks off signup so the redirect
// (which runs after page reload) doesn't have to wait on GrowthBook re-init.
export const persistPostSignupFlow = (navType) => {
  if (navType === 'paywall' || navType === 'chat' || navType === 'chat_paywall') {
    localStorage.setItem(NAV_STORAGE_KEY, navType);
  }
};

export const resolvePostSignupRedirect = () => {
  const character = getSelectedCharacterSlug();
  const navType = getNavType();

  // chat_paywall → flat /lobby, model carried as selected_slug in the query.
  if (navType === 'chat_paywall') {
    const url = new URL(`${SWIPEY_BASE_URL}/lobby`);
    url.searchParams.set('from_landing', 'true');
    // Tag the /lobby handoff so the lobby flow/analytics can identify the source.
    url.searchParams.set('type', 'chat_paywall');
    if (character) url.searchParams.set('selected_slug', character);
    return url.toString();
  }

  // plain chat → straight to the character chat.
  if (navType === 'chat') {
    return `${SWIPEY_BASE_URL}/chats/${character}`;
  }

  return `${SWIPEY_BASE_URL}/paywall?from_landing=true&character=${character}`;
};

// Rewrites a fully-built redirect URL (typically a paywall URL with auth_token / selected_slug
// / utm) into the chat destination when the nav type bypasses the paywall. Returns the
// original URL otherwise.
export const applyPostSignupFlow = (defaultUrl, opts = {}) => {
  const navType = getNavType();
  if (!isChatNav(navType) || !defaultUrl) return defaultUrl;

  // chat_paywall: keep the fully-built paywall URL exactly as-is (auth_token, selected_slug,
  // utm, theme, plans, …) and only swap the destination to the flat /lobby.
  if (navType === 'chat_paywall') {
    try {
      const url = new URL(defaultUrl);
      url.pathname = '/lobby';
      // Tag the /lobby handoff; overrides the style type baked into the paywall URL.
      url.searchParams.set('type', 'chat_paywall');
      return url.toString();
    } catch {
      return defaultUrl;
    }
  }

  // plain chat: route straight to the character chat, carrying only auth_token/auth_type
  // (model-params deliberately omitted — see the xDate-caching note in api.js).
  const character = getSelectedCharacterSlug();
  const chatUrl = new URL(`${SWIPEY_BASE_URL}/chats/${character}`);

  let token = opts.authToken;
  let type = opts.authType;
  try {
    const u = new URL(defaultUrl);
    if (!token) token = u.searchParams.get('auth_token');
    if (!type) type = u.searchParams.get('auth_type');
  } catch {}

  if (token) chatUrl.searchParams.set('auth_token', token);
  if (type) chatUrl.searchParams.set('auth_type', type);

  return chatUrl.toString();
};

// For the Google OAuth flow: the lander encodes a `path` in the OAuth state, backend builds
// `BASE + path` as the post-OAuth redirect. We swap /paywall → the chat destination,
// preserving any query string. chat_paywall → flat /lobby; chat → /chats/{slug}.
export const applyPostSignupFlowToPath = (defaultPath) => {
  const navType = getNavType();
  if (!isChatNav(navType) || !defaultPath) return defaultPath;

  const queryIndex = defaultPath.indexOf('?');
  const query = queryIndex >= 0 ? defaultPath.slice(queryIndex) : '';

  // chat_paywall → flat /lobby. Tag it with type=chat_paywall (overriding any style type in the
  // existing query); selected_slug et al. are preserved.
  if (navType === 'chat_paywall') {
    const params = new URLSearchParams(query);
    params.set('type', 'chat_paywall');
    return `/lobby?${params.toString()}`;
  }

  // plain chat → /chats/{slug}, keeping the query.
  const character = getSelectedCharacterSlug();
  return `/chats/${character}${query}`;
};
